import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabase';
import { speakText } from '../utils/audioUtils';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import {
  fetchWritingCategories,
  fetchWritingExercises,
  saveWritingResponse,
  WritingCategory,
  WritingExercise,
} from '../api/writingApi';
import './WritingScreen.css';

type Lang = 'bn' | 'it' | 'en';

function getLang<T extends Record<string, string> | null | undefined>(obj: T, lang: Lang): string {
  if (!obj) return '';
  return obj[lang] || obj['en'] || obj['it'] || Object.values(obj)[0] || '';
}

const CONTENT = {
  bn: {
    title: 'লেখা অনুশীলন',
    searchCategories: 'বিভাগ খুঁজুন...',
    searchExercises: 'অনুশীলন খুঁজুন...',
    prompt: 'প্রশ্ন',
    yourResponse: 'আপনার উত্তর (ইতালীয় ভাষায় লিখুন)',
    placeholder: 'ইতালীয় ভাষায় আপনার উত্তর লিখুন...',
    submit: 'জমা দিন',
    showAnswer: 'উদাহরণ উত্তর দেখুন',
    hideAnswer: 'উত্তর লুকান',
    exampleAnswer: 'উদাহরণ উত্তর',
    noExercises: 'কোনো অনুশীলন পাওয়া যায়নি।',
    responseRequired: 'অনুগ্রহ করে একটি উত্তর লিখুন',
    beginner: 'শিক্ষার্থী',
    intermediate: 'মধ্যবর্তী',
    advanced: 'উন্নত',
  },
  it: {
    title: 'Pratica Scrittura',
    searchCategories: 'Cerca categorie...',
    searchExercises: 'Cerca esercizi...',
    prompt: 'Istruzione',
    yourResponse: 'La tua risposta (in italiano)',
    placeholder: 'Scrivi la tua risposta in italiano...',
    submit: 'Invia',
    showAnswer: 'Vedi risposta esempio',
    hideAnswer: 'Nascondi risposta',
    exampleAnswer: 'Risposta Esempio',
    noExercises: 'Nessun esercizio trovato.',
    responseRequired: 'Per favore scrivi una risposta',
    beginner: 'Principiante',
    intermediate: 'Intermedio',
    advanced: 'Avanzato',
  },
  en: {
    title: 'Writing Practice',
    searchCategories: 'Search categories...',
    searchExercises: 'Search exercises...',
    prompt: 'Prompt',
    yourResponse: 'Your Response (write in Italian)',
    placeholder: 'Write your response in Italian...',
    submit: 'Submit',
    showAnswer: 'Show Example Answer',
    hideAnswer: 'Hide Answer',
    exampleAnswer: 'Example Answer',
    noExercises: 'No exercises found.',
    responseRequired: 'Please write a response',
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
  },
};

const DIFFICULTY_COLORS: Record<string, string> = {
  Beginner: '#10b981',
  Intermediate: '#f59e0b',
  Advanced: '#ef4444',
};

const WritingScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const lang = (currentLanguage as Lang) || 'bn';
  const c = CONTENT[lang] || CONTENT.en;

  const [categories, setCategories] = useState<WritingCategory[]>([]);
  const [exercises, setExercises] = useState<WritingExercise[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<WritingCategory | null>(null);
  const [selectedExercise, setSelectedExercise] = useState<WritingExercise | null>(null);
  const [userResponse, setUserResponse] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setUserId(user.id);
    });
    fetchWritingCategories().then(data => {
      setCategories(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      setLoading(true);
      fetchWritingExercises(selectedCategory.id).then(data => {
        setExercises(data);
        setLoading(false);
      });
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedExercise) {
      setUserResponse('');
      setShowAnswer(false);
    }
  }, [selectedExercise]);

  const handleBack = () => {
    if (selectedExercise) {
      setSelectedExercise(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
      setSearchQuery('');
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = async () => {
    if (!userResponse.trim()) {
      alert(c.responseRequired);
      return;
    }
    if (userId && selectedExercise) {
      await saveWritingResponse(userId, selectedExercise.id, userResponse);
    }
    setShowAnswer(true);
  };

  const handleSpeak = async (text: string) => {
    if (isSpeaking) return;
    setIsSpeaking(true);
    try {
      await speakText(text, 'answer');
    } finally {
      setIsSpeaking(false);
    }
  };

  const headerTitle = selectedExercise
    ? getLang(selectedExercise.title_ml, lang)
    : selectedCategory
    ? getLang(selectedCategory.name_ml, lang)
    : c.title;

  // ── Categories list ──────────────────────────────────────────────────────────
  const filteredCategories = categories.filter(cat =>
    getLang(cat.name_ml, lang).toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ── Exercises list ───────────────────────────────────────────────────────────
  const filteredExercises = exercises.filter(ex =>
    getLang(ex.title_ml, lang).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const difficultyLabel = (d: string) => {
    if (d === 'Beginner') return c.beginner;
    if (d === 'Intermediate') return c.intermediate;
    return c.advanced;
  };

  return (
    <div className="writing-screen">
      {/* Header */}
      <div className="screen-header">
        <button className="header-btn back-btn" onClick={handleBack} aria-label="Go back">
          ←
        </button>
        <div className="header-center">
          <h1>{headerTitle}</h1>
        </div>
        <button className="header-btn settings-btn" onClick={() => navigate('/settings')} aria-label="Open settings">
          ⋯
        </button>
      </div>

      <LessonBannerAd />

      {/* ── Categories view ── */}
      {!selectedCategory && !selectedExercise && (
        <div className="writing-content">
          <div className="writing-search-bar">
            <span className="writing-search-icon">🔍</span>
            <input
              type="text"
              placeholder={c.searchCategories}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="writing-loading">⏳</div>
          ) : (
            <div className="writing-categories-grid">
              {filteredCategories.map(cat => (
                <div
                  key={cat.id}
                  className="writing-category-card"
                  style={{ '--cat-color': cat.color } as React.CSSProperties}
                  onClick={() => { setSelectedCategory(cat); setSearchQuery(''); }}
                >
                  <div className="writing-category-icon" style={{ background: cat.color }}>
                    {cat.icon_name || cat.icon || '✍️'}
                  </div>
                  <div className="writing-category-info">
                    <div className="writing-category-it">{getLang(cat.name_ml, 'it')}</div>
                    <div className="writing-category-native">{getLang(cat.name_ml, lang)}</div>
                    <div className="writing-category-desc">{getLang(cat.description_ml, lang)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Exercises list view ── */}
      {selectedCategory && !selectedExercise && (
        <div className="writing-content">
          <div className="writing-search-bar">
            <span className="writing-search-icon">🔍</span>
            <input
              type="text"
              placeholder={c.searchExercises}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="writing-loading">⏳</div>
          ) : filteredExercises.length === 0 ? (
            <div className="writing-empty">{c.noExercises}</div>
          ) : (
            <div className="writing-exercises-list">
              {filteredExercises.map(ex => (
                <div
                  key={ex.id}
                  className="writing-exercise-card"
                  onClick={() => setSelectedExercise(ex)}
                >
                  <div className="writing-exercise-card-top">
                    <div className="writing-exercise-titles">
                      <span className="writing-exercise-it">{getLang(ex.title_ml, 'it')}</span>
                      <span className="writing-exercise-native">{getLang(ex.title_ml, lang)}</span>
                    </div>
                    <span
                      className="writing-difficulty-badge"
                      style={{ background: DIFFICULTY_COLORS[ex.difficulty] || '#6b7280' }}
                    >
                      {difficultyLabel(ex.difficulty)}
                    </span>
                  </div>
                  <p className="writing-exercise-preview">
                    {getLang(ex.prompt_ml, lang).substring(0, 90)}…
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Exercise detail view ── */}
      {selectedExercise && (
        <div className="writing-content">
          <div className="writing-exercise-detail">
            {/* Difficulty badge */}
            <span
              className="writing-difficulty-badge"
              style={{ background: DIFFICULTY_COLORS[selectedExercise.difficulty] || '#6b7280' }}
            >
              {difficultyLabel(selectedExercise.difficulty)}
            </span>

            {/* Prompt */}
            <div className="writing-prompt-card">
              <h3 className="writing-prompt-label">{c.prompt}</h3>
              <p className="writing-prompt-text">{getLang(selectedExercise.prompt_ml, lang)}</p>
              {/* Italian prompt hint */}
              {lang !== 'it' && (
                <p className="writing-prompt-it">{getLang(selectedExercise.prompt_ml, 'it')}</p>
              )}
            </div>

            {/* Response textarea */}
            <div className="writing-response-section">
              <label className="writing-response-label">{c.yourResponse}</label>
              <textarea
                className="writing-response-input"
                value={userResponse}
                onChange={e => setUserResponse(e.target.value)}
                placeholder={c.placeholder}
                rows={7}
                disabled={showAnswer}
              />
            </div>

            {/* Actions */}
            <div className="writing-actions">
              {!showAnswer ? (
                <button
                  className="writing-submit-btn"
                  onClick={handleSubmit}
                  disabled={!userResponse.trim()}
                >
                  ✓ {c.submit}
                </button>
              ) : (
                <button
                  className="writing-toggle-answer-btn"
                  onClick={() => setShowAnswer(v => !v)}
                >
                  {showAnswer ? c.hideAnswer : c.showAnswer}
                </button>
              )}
            </div>

            {/* Example answer */}
            {showAnswer && (
              <div className="writing-answer-card">
                <div className="writing-answer-header">
                  <h4 className="writing-answer-title">🇮🇹 {c.exampleAnswer}</h4>
                  <button
                    className={`writing-speak-btn ${isSpeaking ? 'speaking' : ''}`}
                    onClick={() => handleSpeak(getLang(selectedExercise.example_answer_ml, 'it'))}
                    aria-label="Pronounce"
                  >
                    🔊
                  </button>
                </div>
                <p
                  className="writing-answer-text"
                  onClick={() => handleSpeak(getLang(selectedExercise.example_answer_ml, 'it'))}
                >
                  {getLang(selectedExercise.example_answer_ml, 'it')}
                </p>
                {lang !== 'it' && (
                  <p className="writing-answer-translation">
                    {getLang(selectedExercise.example_answer_ml, lang)}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default WritingScreen;
